import type React from "react";
import { timelineDuration, timelineToSource, sourceToTimeline, overlaySpan, type Clip } from "@hudhud/core";
import { useEditor } from "../store.js";
import { slotAt, type Block } from "./model.js";

const MIN_RANGE_MS = 500;

/** One pointer drag: `move` gets the distance from the press, `end` says whether it ever moved. The strip never pans meanwhile. */
export function startDrag(e: React.PointerEvent, on: { move: (dx: number, dy: number, ev: PointerEvent) => void; end: (moved: boolean, ev: PointerEvent) => void; threshold?: number }): void {
  e.stopPropagation(); e.preventDefault();
  const x0 = e.clientX, y0 = e.clientY, th = on.threshold ?? 3;
  let moved = false;
  const move = (ev: PointerEvent) => { const dx = ev.clientX - x0, dy = ev.clientY - y0; if (!moved && Math.abs(dx) + Math.abs(dy) < th) return; moved = true; on.move(dx, dy, ev); };
  const up = (ev: PointerEvent) => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up); on.end(moved, ev); };
  window.addEventListener("pointermove", move); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up);
}
const commit = () => useEditor.getState().update((c) => c);
const snapTo = (ms: number) => { const st = useEditor.getState(); if (!st.snap || !st.clip) return ms; const b = st.clip.words.flatMap((w) => [w.startMs, w.endMs]); return b.reduce((best, x) => (Math.abs(x - ms) < Math.abs(best - ms) ? x : best), ms); };

/** The selected piece's end handles: the clip's trim on the outer ones, the cut point on an inner one. */
export function dragTrim(e: React.PointerEvent, clip: Clip, i: number, edge: "start" | "end", px: number): void {
  const seg = clip.segments[i];
  const isFirst = i === 0 && edge === "start", isLast = i === clip.segments.length - 1 && edge === "end";
  startDrag(e, {
    move: (dx) => {
      const st = useEditor.getState();
      const delta = dx / px;
      if (isFirst) { const v = snapTo(seg.inMs + delta); st.update((c) => ({ ...c, edits: { ...c.edits!, trimStartMs: Math.min(v, c.edits!.trimEndMs - 2000) } }), { transient: true }); return; }
      if (isLast) { const v = snapTo(seg.outMs + delta); st.update((c) => ({ ...c, edits: { ...c.edits!, trimEndMs: Math.max(v, c.edits!.trimStartMs + 2000) } }), { transient: true }); return; }
      const gi = edge === "end" ? i : i - 1;
      const a = clip.segments[gi], b = clip.segments[gi + 1];
      if (!a || !b) return;
      const gap = edge === "end" ? { inMs: Math.min(snapTo(seg.outMs + delta), b.inMs - 40), outMs: b.inMs } : { inMs: a.outMs, outMs: Math.max(snapTo(seg.inMs + delta), a.outMs + 40) };
      st.update(() => clip, { transient: true }); // always measured from the pieces the drag started with
      st.setGap(gi, gap, { transient: true }); // one history step for the whole drag, written when it ends
    },
    end: (moved) => { if (moved) commit(); },
  });
}

/** Drag the selected piece along the strip to play it earlier or later. `originX` gives the screen x of time 0. */
export function dragReorder(e: React.PointerEvent, i: number, blocks: Block[], px: number, originX: () => number, setDrop: (slot: number | null) => void, onTap: () => void): void {
  const slot = (clientX: number) => slotAt(blocks, (clientX - originX()) / px);
  startDrag(e, {
    threshold: 8,
    move: (_dx, _dy, ev) => setDrop(slot(ev.clientX)),
    end: (moved, ev) => { setDrop(null); if (!moved) { onTap(); return; } let to = slot(ev.clientX); if (to > i) to -= 1; if (to !== i) useEditor.getState().movePieceTo(i, to); },
  });
}

/** A picture or a block of words behind the speaker: drag it to move, or an edge to stretch it, in timeline time (so it runs across cuts). */
export function dragRange(e: React.PointerEvent, kind: "picture" | "behind", id: string, mode: "move" | "start" | "end", px: number, onTap: () => void): void {
  const c0 = useEditor.getState().clip!;
  const item = kind === "picture" ? c0.overlays.find((o) => o.id === id) : c0.behindTexts.find((t) => t.id === id);
  const span = item ? overlaySpan(c0.segments, item) : null;
  if (!item || !span) return;
  const total = timelineDuration(c0.segments), len = span.outMs - span.inMs;
  startDrag(e, {
    move: (dx) => {
      const d = dx / px;
      let a = span.inMs, b = span.outMs;
      if (mode === "move") { a = Math.max(0, Math.min(total - len, span.inMs + d)); b = a + len; }
      else if (mode === "start") a = Math.max(0, Math.min(span.outMs - MIN_RANGE_MS, span.inMs + d));
      else b = Math.max(span.inMs + MIN_RANGE_MS, Math.min(total, span.outMs + d));
      const startMs = Math.round(timelineToSource(c0.segments, a)), endMs = Math.round(timelineToSource(c0.segments, b));
      useEditor.getState().update((c) => (kind === "picture" ? { ...c, overlays: c.overlays.map((o) => (o.id === id ? { ...o, startMs, endMs } : o)) } : { ...c, behindTexts: c.behindTexts.map((t) => (t.id === id ? { ...t, startMs, endMs } : t)) }), { transient: true });
    },
    end: (moved) => { if (moved) commit(); else onTap(); },
  });
}

/** One of the owner's own quiet ranges of the BG audio: move it, or stretch an edge. */
export function dragQuiet(e: React.PointerEvent, index: number, mode: "move" | "start" | "end", px: number, speechMs: number, onTap: () => void): void {
  const clip = useEditor.getState().clip!;
  const m0 = clip.edits!.musicMutes[index];
  if (!m0) return;
  const segs = clip.segments;
  const a0 = sourceToTimeline(segs, m0.inMs) ?? 0, b0 = sourceToTimeline(segs, m0.outMs) ?? a0 + 500;
  startDrag(e, {
    move: (dx) => {
      const d = dx / px;
      let a = a0, b = b0;
      if (mode === "move") { a = Math.max(0, Math.min(speechMs - (b0 - a0), a0 + d)); b = a + (b0 - a0); }
      else if (mode === "start") a = Math.max(0, Math.min(b0 - 200, a0 + d));
      else b = Math.max(a0 + 200, Math.min(speechMs, b0 + d));
      useEditor.getState().setMusicMute(index, { inMs: timelineToSource(segs, a), outMs: timelineToSource(segs, b) });
    },
    end: (moved) => { if (moved) commit(); else onTap(); },
  });
}

/** A directed beat slides along the strip and lands on the source moment under it. */
export function dragBeat(e: React.PointerEvent, id: string, tlAtMs: number, px: number, speechMs: number, onTap: () => void): void {
  startDrag(e, {
    move: (dx) => { const tl = Math.max(0, Math.min(speechMs - 1, tlAtMs + dx / px)); const st = useEditor.getState(); st.moveMoment(id, timelineToSource(st.clip!.segments, tl)); },
    end: (moved) => { if (moved) commit(); else onTap(); },
  });
}

/** The opener's length (its right handle) or the end card's. */
export function dragLength(e: React.PointerEvent, which: "opener" | "end", px: number): void {
  const st = useEditor.getState();
  const start = which === "opener" ? st.clip!.hook.durationMs : st.clip!.endCard.durationMs;
  startDrag(e, {
    move: (dx) => { const s = useEditor.getState(); if (which === "opener") s.setHookDuration(start + dx / px); else s.setEndDuration(start + dx / px); },
    end: (moved) => { if (moved) commit(); },
  });
}
